import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaBook,
  FaEye,
  FaList,
  FaFilePdf,
  FaTrash,
  FaArrowLeft,
} from "react-icons/fa";
import { Document, Page, pdfjs } from "react-pdf";
import styles from "./Book.module.css";
import { useSelector } from "react-redux";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../services/Api";
import { CustomButton } from "../../../components";
import PopupModal from "../../../components/PopupModal/PopupModal";

// PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BookView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";

  const stateData = location.state || {};
  const bookId = stateData?.ID || stateData?.data?.id || "";

  const [bookInfo, setBookInfo] = useState(null);
  const [bookContent, setBookContent] = useState([]);
  const [loading, setLoading] = useState(false);

  // PDF viewer state
  const [pdfUrl, setPdfUrl] = useState("");
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfStartPage, setPdfStartPage] = useState(null);
  const [pdfEndPage, setPdfEndPage] = useState(null);
  const [totalPdfPages, setTotalPdfPages] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);

  // Delete modal state
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
  });

  // ─────────────────────────────────────────────
  //  Build full PDF URL from API path
  // ─────────────────────────────────────────────
  const buildPdfUrl = (filePath) => {
    if (!filePath) return "";
    // If already a full URL, return as-is
    if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
      return filePath;
    }
    // Prepend IMAGE_BASE_URL to relative path
    const base = IMAGE_BASE_URL.endsWith("/")
      ? IMAGE_BASE_URL.slice(0, -1)
      : IMAGE_BASE_URL;
    const path = filePath.startsWith("/") ? filePath : `/${filePath}`;
    return `${base}${path}`;
  };

  // ─────────────────────────────────────────────
  //  Fetch book details
  // ─────────────────────────────────────────────
  const fetchBookView = useCallback(async () => {
    if (!bookId || !school_id) return;
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/book/view", {
        id: bookId,
        school_id: Number(school_id),
      });

      if (response?.data?.status && response?.data?.data) {
        const info = response.data.data.book_info?.[0] || null;
        const content = response.data.data.book_content || [];
        setBookInfo(info);
        setBookContent(content);
        if (info?.uploaded_file_path) {
          const filePath = info.uploaded_file_path;
          if (filePath.startsWith("/") || /^https?:\/\//i.test(filePath)) {
            // legacy local file — served as a normal URL
            setPdfUrl(buildPdfUrl(filePath));
          } else {
            // Azure blob — fetch through the authenticated API (blob logic)
            try {
              const pdfResponse = await AxiosInstance.get(
                `/content/image?path=${encodeURIComponent(filePath)}`,
                { responseType: "blob" },
              );
              const objectUrl = URL.createObjectURL(
                new Blob([pdfResponse.data], { type: "application/pdf" }),
              );
              setPdfUrl(objectUrl);
            } catch (pdfError) {
              console.error("Error loading book PDF from blob:", pdfError);
              setPdfUrl("");
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching book view:", error);
    } finally {
      setLoading(false);
    }
  }, [bookId, school_id]);

  useEffect(() => {
    fetchBookView();
  }, [fetchBookView]);

  // ─────────────────────────────────────────────
  //  Delete book
  // ─────────────────────────────────────────────
  const handleDeleteClick = () => {
    setModalState({
      open: true,
      type: "Confirm",
      message:
        translations?.labels?.confirmDelete ||
        "Are you sure you want to delete?",
    });
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.delete("/book/delete", {
        data: {
          id: bookId,
          school_id: Number(school_id),
          deleted_by: user?.id,
        },
      });

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            translations?.labels?.deleteSuccess || "Deleted successfully!",
        });
      } else {
        setModalState({
          open: true,
          type: "Failed",
          message:
            response?.data?.message ||
            translations?.labels?.deleteFailed ||
            "Failed to delete.",
        });
      }
    } catch (error) {
      console.error("Error deleting book:", error);
      setModalState({
        open: true,
        type: "Failed",
        message:
          translations?.labels?.somethingWentWrong || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = (confirmed) => {
    if (confirmed && modalState.type === "Confirm") {
      handleDeleteConfirm();
      return;
    }
    if (modalState.type === "success") {
      navigate("/user/bookList");
    }
    setModalState({ open: false, type: "", message: "" });
  };

  // ─────────────────────────────────────────────
  //  PDF handlers
  // ─────────────────────────────────────────────
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleTopicView = (item) => {
    setSelectedTopic(item);
    setPdfStartPage(item.pdf_start_page);
    setPdfEndPage(item.pdf_end_page);
    setCurrentPage(item.pdf_start_page);
    setTotalPdfPages(item.pdf_end_page - item.pdf_start_page + 1);
  };

  // FULL PDF — opens in new browser tab
  const handleFullPdf = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handlePrevPage = () => {
    if (currentPage > (pdfStartPage || 1)) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < (pdfEndPage || numPages)) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleBack = () => {
    navigate("/user/bookList");
  };
  const handleEdit = () => {
    navigate(`/user/editBook/${bookId}`);
  };

  // Current page position within the selected range
  const currentPageInRange = pdfStartPage
    ? currentPage - pdfStartPage + 1
    : currentPage;
  const totalInRange = totalPdfPages || numPages || 0;

  if (loading && !bookInfo) {
    return (
      <div className={styles.loadingContainer}>
        {translations?.labels?.loading || "Loading..."}
      </div>
    );
  }

  return (
    <div>
      {/* Confirm / Success / Failed PopupModal */}
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations?.labels?.success || "Success"
            : modalState.type === "Confirm"
              ? translations?.labels?.delete || "DELETE"
              : translations?.labels?.failed || "Failed"
        }
        message={modalState.message}
        onClose={handleModalClose}
      />

      {/* TOP ACTION BUTTONS — small, right-aligned */}
      <div className={styles.topActions}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.backBtnSmall}
          onClick={handleBack}
          fullWidth={false}
        >
          <FaArrowLeft style={{ marginRight: "6px" }} />
          {translations?.labels?.back || "BACK"}
        </CustomButton>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.backBtnSmall}
          onClick={handleEdit}
          fullWidth={false}
        >
          {/* <FaArrowLeft style={{ marginRight: "6px" }} /> */}
          {translations?.labels?.edit || "Edit"}
        </CustomButton>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.deleteBtnSmall}
          onClick={handleDeleteClick}
          fullWidth={false}
        >
          <FaTrash style={{ marginRight: "6px" }} />
          {translations?.labels?.delete || "DELETE"}
        </CustomButton>
      </div>

      {/* MAIN LAYOUT */}
      <div className={styles.viewLayout}>
        {/* LEFT PANEL — Book Info + Table of Contents */}
        <div className={styles.viewLeftPanel}>
          <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.headerIcon}>
                <FaBook />
              </div>
              <h1 className={styles.headerTitle}>
                {translations?.labels?.viewBook || "View Book"}
              </h1>
            </div>

            {/* Book Info Grid */}
            {bookInfo && (
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translations?.labels?.class || "Class"}
                  </span>
                  <span className={styles.infoValue}>
                    {bookInfo.class || "-"}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translations?.labels?.subject || "Subject"}
                  </span>
                  <span className={styles.infoValue}>
                    {bookInfo.subject || "-"}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translations?.labels?.subjectMedium || "Subject Medium"}
                  </span>
                  <span className={styles.infoValue}>
                    {bookInfo.medium || "-"}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translations?.labels?.volume || "Volume"}
                  </span>
                  <span className={styles.infoValue}>
                    {bookInfo.volume || "-"}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translations?.labels?.term || "Term"}
                  </span>
                  <span className={styles.infoValue}>
                    {bookInfo.term || "-"}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translations?.labels?.totalPages || "Total Pages"}
                  </span>
                  <span className={styles.infoValue}>
                    {bookInfo.total_pages || "-"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Table of Contents */}
          <div className={styles.container} style={{ marginTop: "16px" }}>
            <div className={styles.header}>
              <div className={styles.headerIcon}>
                <FaList />
              </div>
              <h1 className={styles.headerTitle}>
                {translations?.labels?.tableOfContents || "Table of Contents"}
              </h1>
            </div>

            <div className={styles.tocTableWrapper}>
              <table className={styles.tocTable}>
                <thead>
                  <tr>
                    <th>{translations?.labels?.serialNo || "S.No"}</th>
                    <th>{translations?.labels?.topic || "Topic"}</th>
                    <th>{translations?.labels?.subTopic || "Sub Topic"}</th>
                    <th>{translations?.labels?.start || "Start"}</th>
                    <th>{translations?.labels?.end || "End"}</th>
                    <th>
                      {translations?.labels?.completionMonth ||
                        "Completion Month"}
                    </th>
                    <th>{translations?.labels?.view || "View"}</th>
                  </tr>
                </thead>
                <tbody>
                  {bookContent.length > 0 ? (
                    bookContent.map((item, index) => (
                      <tr
                        key={item.id || index}
                        className={
                          selectedTopic?.id === item.id
                            ? styles.tocRowActive
                            : styles.tocRow
                        }
                      >
                        <td>{index + 1}</td>
                        <td>{item.topic || "-"}</td>
                        <td>{item.sub_topic || "-"}</td>
                        <td>{item.item_start_page}</td>
                        <td>{item.item_end_page}</td>
                        <td>{item.month ? item.month : "-"}</td>
                        <td>
                          <button
                            className={styles.tocViewBtn}
                            onClick={() => handleTopicView(item)}
                            title={translations?.labels?.view || "View"}
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className={styles.noRecords}>
                        {translations?.labels?.noRecordsToDisplay ||
                          "No records to display"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL — PDF Viewer */}
        <div className={styles.viewRightPanel}>
          <div className={styles.pdfContainer}>
            {/* PDF Header */}
            <div className={styles.pdfHeader}>
              <div className={styles.pdfHeaderInfo}>
                {selectedTopic ? (
                  <>
                    <span className={styles.pdfTopicName}>
                      {selectedTopic.topic}
                    </span>
                    <span className={styles.pdfPageRange}>
                      {translations?.labels?.bookPages || "Book Pages"}{" "}
                      {selectedTopic.item_start_page} –{" "}
                      {selectedTopic.item_end_page} |{" "}
                      {translations?.labels?.pdfPages || "PDF Pages"}{" "}
                      {selectedTopic.pdf_start_page} –{" "}
                      {selectedTopic.pdf_end_page}
                    </span>
                  </>
                ) : (
                  <span className={styles.pdfTopicName}>
                    {translations?.labels?.selectTopicToView ||
                      "Select a topic to view"}
                  </span>
                )}
              </div>
              <button
                className={styles.fullPdfBtnSmall}
                onClick={handleFullPdf}
                disabled={!pdfUrl}
                title="Open full PDF in new tab"
              >
                <FaFilePdf style={{ marginRight: "4px" }} />
                {translations?.labels?.fullPdf || "FULL PDF"}
              </button>
            </div>

            {/* PDF Navigation — only when a topic is selected */}
            {selectedTopic && pdfUrl && (
              <div className={styles.pdfNavBar}>
                <button
                  className={styles.pdfNavBtn}
                  onClick={handlePrevPage}
                  disabled={currentPage <= (pdfStartPage || 1)}
                >
                  {"< "}
                  {translations?.labels?.prev || "Prev"}
                </button>
                <span className={styles.pdfPageInfo}>
                  {translations?.labels?.page || "Page"} {currentPage} (
                  {currentPageInRange} {translations?.labels?.of || "of"}{" "}
                  {totalInRange})
                </span>
                <button
                  className={styles.pdfNavBtn}
                  onClick={handleNextPage}
                  disabled={currentPage >= (pdfEndPage || numPages)}
                >
                  {translations?.labels?.next || "Next"}
                  {" >"}
                </button>
                <div className={styles.pdfZoomControls}>
                  <button className={styles.pdfZoomBtn} onClick={handleZoomOut}>
                    −
                  </button>
                  <span className={styles.pdfZoomLevel}>
                    {Math.round(scale * 100)}%
                  </span>
                  <button className={styles.pdfZoomBtn} onClick={handleZoomIn}>
                    +
                  </button>
                </div>
              </div>
            )}

            {/* PDF Document */}
            <div className={styles.pdfViewer}>
              {pdfUrl && selectedTopic ? (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(error) =>
                    console.error("PDF load error:", error)
                  }
                  loading={
                    <div className={styles.pdfLoading}>
                      {translations?.labels?.loading || "Loading PDF..."}
                    </div>
                  }
                  error={
                    <div className={styles.pdfError}>
                      {translations?.labels?.failedToLoadPdf ||
                        "Failed to load PDF"}
                    </div>
                  }
                >
                  <Page
                    pageNumber={currentPage}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading={
                      <div className={styles.pdfLoading}>
                        {translations?.labels?.loading || "Loading page..."}
                      </div>
                    }
                  />
                </Document>
              ) : (
                <div className={styles.pdfPlaceholder}>
                  <div className={styles.pdfPlaceholderIcons}>
                    <FaList size={32} color="#adb5bd" />
                    <FaFilePdf size={32} color="#adb5bd" />
                  </div>
                  <p className={styles.pdfPlaceholderText}>
                    {translations?.labels?.clickEyeIconToView || "Click the"}{" "}
                    <span
                      style={{ color: "var(--color-error)", fontWeight: 600 }}
                    >
                      {translations?.labels?.eyeIcon || "eye icon"}
                    </span>{" "}
                    {translations?.labels?.onAnyTopicToViewPages ||
                      "on any topic to view its pages here"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookView;
